
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SubscriptionStatus from './SubscriptionStatus';
import ManageSubscriptionDialog from './ManageSubscriptionDialog';
import { useSubscriptionManagement } from "@/hooks/account/useSubscriptionManagement";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { isIOSDevice } from "@/utils/deviceDetection";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

interface SubscriptionAreaProps {
  status: "active" | "inactive";
  plan: "monthly" | "yearly" | null;
  nextBillingDate: string | null;
  subscriptionId: string | null;
  isCancelled: boolean;
}

const SubscriptionArea = ({ 
  status, 
  plan, 
  nextBillingDate,
  subscriptionId,
  isCancelled
}: SubscriptionAreaProps) => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isIOS = isIOSDevice();
  
  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };
  
  const handlePremiumAction = () => {
    if (status === "active") {
      // If user has an active subscription, show the manage dialog
      handleOpenDialog();
    } else {
      // If user doesn't have an active subscription, redirect to payment page
      navigate('/payment', {
        state: {
          isYearly: true // Default to yearly plan
        }
      });
    }
  };
  
  return (
    <Card className="shadow overflow-hidden border-0">
      <CardHeader className="pb-2 px-6 pt-6 bg-gradient-to-r from-[#F3F7FC] to-white">
        <CardTitle className="text-xl font-semibold text-[#4A607B]">Subscription Status</CardTitle>
      </CardHeader>
      
      <CardContent className="px-6 pb-6">
        <SubscriptionStatus 
          status={status} 
          plan={plan}
          nextBillingDate={nextBillingDate}
          subscriptionId={subscriptionId}
          isCancelled={isCancelled}
          onPremiumAction={handlePremiumAction}
        />
        
        {isIOS && (
          <Alert className="mt-4 bg-blue-50 border-blue-200">
            <InfoIcon className="h-4 w-4 text-blue-500" />
            <AlertDescription className="text-sm text-gray-700">
              <strong>iOS Users:</strong> To subscribe or manage your premium subscription, 
              please visit our website at <a href="https://metrade.io" 
              className="text-blue-600 underline font-medium" target="_blank" rel="noopener noreferrer">
                metrade.io
              </a>
            </AlertDescription>
          </Alert>
        )}
        
        {status === "active" && (
          <ManageSubscriptionDialog 
            isOpen={isDialogOpen}
            onClose={handleCloseDialog}
            subscriptionId={subscriptionId}
            plan={plan}
            nextBillingDate={nextBillingDate}
            isCancelled={isCancelled}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionArea;
